import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import emailService from '../services/emailService';
import './ChatBot.css';

// Composant pour rendre le contenu avec support HTML
const MessageContent = ({ content, onHasTable }) => {
  // Détecter si le contenu contient des tableaux HTML
  const hasHtmlTable = content.includes('<table') || content.includes('<th') || content.includes('<td');

  // Notifier le parent si on a un tableau
  useEffect(() => {
    if (onHasTable) {
      onHasTable(hasHtmlTable);
    }
  }, [hasHtmlTable, onHasTable]);

  if (hasHtmlTable) {
    return (
      <div
        className="message-html-content"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  // Rendu normal pour le texte
  return <div className="whitespace-pre-wrap text-sm leading-relaxed">{content}</div>;
};

// Composant wrapper pour gérer l'état hasTable de chaque message
const MessageWrapper = ({ message, onActionClick }) => {
  const [hasTable, setHasTable] = useState(false);
  const [exportLoading, setExportLoading] = useState({
    pdf: false,
    txt: false
  });

  const getResponseTypeIcon = (responseType) => {
    const icons = {
      search: 'bi-search',
      stats: 'bi-graph-up',
      summary: 'bi-file-text',
      sentiment: 'bi-emoji-smile',
      urgent: 'bi-exclamation-triangle',
      contacts: 'bi-people',
      general: 'bi-chat-dots',
      error: 'bi-exclamation-circle'
    };
    return icons[responseType] || 'bi-chat-dots';
  };

  const parseHtmlTable = (htmlContent) => {
    // Créer un élément DOM temporaire pour parser le HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    const tables = tempDiv.querySelectorAll('table');
    const parsedTables = [];

    tables.forEach((table, tableIndex) => {
      const tableData = {
        headers: [],
        rows: []
      };

      // Extraire les en-têtes
      const headerCells = table.querySelectorAll('thead th, thead td');
      headerCells.forEach(cell => {
        tableData.headers.push(cell.textContent.trim());
      });

      // Extraire les lignes de données
      const bodyRows = table.querySelectorAll('tbody tr');
      bodyRows.forEach(row => {
        const rowData = [];
        const cells = row.querySelectorAll('td, th');
        cells.forEach(cell => {
          rowData.push(cell.textContent.trim());
        });
        if (rowData.length > 0) {
          tableData.rows.push(rowData);
        }
      });

      parsedTables.push(tableData);
    });

    return parsedTables;
  };

  const handleExportResponse = async (format) => {
    setExportLoading(prev => ({ ...prev, [format]: true }));

    try {
      const content = message.content;
      const timestamp = message.timestamp.toLocaleString('fr-FR');
      const filename = `chatbot_response_${message.id}_${Date.now()}`;

      if (format === 'pdf') {
        // Créer un PDF de la réponse avec jsPDF et autoTable
        const { jsPDF } = await import('jspdf');

        // Importer autoTable pour les tableaux
        const autoTable = (await import('jspdf-autotable')).default;

        if (hasTable) {
          // Mode paysage pour les tableaux
          const doc = new jsPDF('landscape');

          // Parser les tableaux HTML
          const tables = parseHtmlTable(content);

          // Extraire le texte sans les tableaux pour l'introduction
          const textWithoutTables = content.replace(/<table[\s\S]*?<\/table>/gi, '[TABLEAU_PLACEHOLDER]');
          const cleanText = textWithoutTables
            .replace(/<[^>]*>/g, ' ')
            .replace(/\s+/g, ' ')
            .replace(/\[TABLEAU_PLACEHOLDER\]/g, '')
            .trim();

          let yPosition = 20;

          // En-tête du document
          doc.setFontSize(16);
          doc.setFont(undefined, 'bold');
          doc.text('Analyse Discover - Rapport Détaillé', 20, yPosition);
          yPosition += 10;

          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');
          doc.text(`Date: ${timestamp}`, 20, yPosition);
          yPosition += 15;

          // Ajouter le texte d'introduction seulement s'il est significatif
          if (cleanText && cleanText.length > 20) {
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('Résumé de l\'analyse:', 20, yPosition);
            yPosition += 8;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            const introLines = doc.splitTextToSize(cleanText, 250);
            doc.text(introLines, 20, yPosition);
            yPosition += introLines.length * 5 + 10;
          }

          // Ajouter chaque tableau avec autoTable
          tables.forEach((tableData, index) => {
            // Vérifier si on a besoin d'une nouvelle page
            if (yPosition > 160) {
              doc.addPage('landscape');
              yPosition = 20;
            }

            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text(`Tableau ${index + 1}: Analyse des Emails`, 20, yPosition);
            yPosition += 10;

            // Préparer les données pour autoTable
            const tableHeaders = tableData.headers.length > 0 ? tableData.headers : ['Données'];
            const tableRows = tableData.rows.length > 0 ? tableData.rows : [['Aucune donnée']];

            // Utiliser autoTable pour créer un tableau professionnel en mode paysage
            doc.autoTable({
              head: [tableHeaders],
              body: tableRows,
              startY: yPosition,
              theme: 'grid',
              headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 9
              },
              bodyStyles: {
                fontSize: 8,
                cellPadding: 2
              },
              margin: { left: 20, right: 20 },
              tableWidth: 'auto',
              styles: {
                overflow: 'linebreak',
                cellWidth: 'wrap',
                fontSize: 8
              },
              columnStyles: {
                // Ajustement automatique des colonnes pour le mode paysage
                0: { cellWidth: 'auto' },
                1: { cellWidth: 'auto' },
                2: { cellWidth: 'auto' },
                3: { cellWidth: 'auto' },
                4: { cellWidth: 'auto' },
                5: { cellWidth: 'auto' }
              }
            });

            yPosition = doc.lastAutoTable.finalY + 15;
          });

          doc.save(`${filename}.pdf`);

        } else {
          // Mode portrait pour le texte simple
          const doc = new jsPDF();

          // Configuration du PDF
          doc.setFontSize(18);
          doc.setFont(undefined, 'bold');
          doc.text('Réponse du Chatbot Discover', 20, 25);

          doc.setFontSize(12);
          doc.setFont(undefined, 'normal');
          doc.text(`Date: ${timestamp}`, 20, 40);
          doc.text(`Type: ${message.responseType || 'Assistant'}`, 20, 50);

          let yPosition = 65;

          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text('Contenu:', 20, yPosition);
          yPosition += 10;

          doc.setFontSize(11);
          doc.setFont(undefined, 'normal');
          const lines = doc.splitTextToSize(content, 170);
          doc.text(lines, 20, yPosition);

          doc.save(`${filename}.pdf`);
        }

      } else if (format === 'txt') {
        // Créer un fichier texte avec tableaux formatés
        let textContent = '';

        if (hasTable) {
          const tables = parseHtmlTable(content);
          const textWithoutTables = content.replace(/<table[\s\S]*?<\/table>/gi, '[TABLEAU]');
          const cleanText = textWithoutTables.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

          if (cleanText && cleanText !== '[TABLEAU]') {
            textContent += cleanText.replace(/\[TABLEAU\]/g, '\n\n[Voir tableau ci-dessous]\n\n') + '\n\n';
          }

          // Ajouter les tableaux formatés
          tables.forEach((tableData, index) => {
            textContent += `${'='.repeat(60)}\n`;
            textContent += `TABLEAU ${index + 1}: ANALYSE DES EMAILS\n`;
            textContent += `${'='.repeat(60)}\n\n`;

            if (tableData.headers.length > 0) {
              // Calculer les largeurs de colonnes
              const colWidths = tableData.headers.map((header, i) => {
                const maxContentLength = Math.max(
                  header.length,
                  ...tableData.rows.map(row => (row[i] || '').length)
                );
                return Math.min(Math.max(maxContentLength + 2, 10), 30);
              });

              // En-têtes
              const headerRow = tableData.headers.map((header, i) =>
                header.padEnd(colWidths[i]).substring(0, colWidths[i])
              ).join(' | ');
              textContent += headerRow + '\n';

              // Ligne de séparation
              const separatorRow = colWidths.map(width => '-'.repeat(width)).join('-+-');
              textContent += separatorRow + '\n';

              // Données
              tableData.rows.forEach(row => {
                const dataRow = row.map((cell, i) =>
                  (cell || '').padEnd(colWidths[i]).substring(0, colWidths[i])
                ).join(' | ');
                textContent += dataRow + '\n';
              });
            }

            textContent += '\n\n';
          });
        } else {
          textContent = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        }

        const fullContent = `${'='.repeat(60)}
RÉPONSE DU CHATBOT DISCOVER
${'='.repeat(60)}

Date: ${timestamp}
Type: ${message.responseType || 'Assistant'}

${'='.repeat(60)}
CONTENU
${'='.repeat(60)}

${textContent}`;

        const blob = new Blob([fullContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      alert('Erreur lors de l\'export de la réponse');
    } finally {
      setExportLoading(prev => ({ ...prev, [format]: false }));
    }
  };

  return (
    <div
      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`${hasTable ? 'message-with-table' : 'chatbot-message-normal'} px-6 py-4 rounded-2xl relative ${message.type === 'user'
            ? 'chatbot-user-message text-white ml-8'
            : message.isError
              ? 'bg-red-50 text-red-800 border border-red-200 mr-8 shadow-md'
              : 'chatbot-bot-message text-slate-800 mr-8'
          }`}
      >
        {/* Icon pour les messages bot */}
        {message.type === 'bot' && !message.isError && (
          <div className="flex items-center space-x-2 mb-2">
            <i className={`${getResponseTypeIcon(message.responseType)} text-blue-600`}></i>
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              {message.responseType || 'Assistant'}
            </span>
            {message.processingTime && (
              <span className="text-xs text-slate-400">
                ({message.processingTime.toFixed(1)}s)
              </span>
            )}
          </div>
        )}

        <MessageContent
          content={message.content}
          onHasTable={setHasTable}
        />

        {/* Boutons d'export pour les réponses du bot */}
        {message.type === 'bot' && !message.isError && (
          <div className="export-buttons">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleExportResponse('pdf')}
                disabled={exportLoading.pdf}
                className="export-button export-button-pdf"
                title="Exporter cette réponse en PDF"
              >
                {exportLoading.pdf ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" style={{ width: '0.6rem', height: '0.6rem' }}></span>
                    Export PDF...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-pdf me-1"></i>
                    Exporter PDF
                  </>
                )}
              </button>

              <button
                onClick={() => handleExportResponse('txt')}
                disabled={exportLoading.txt}
                className="export-button export-button-txt"
                title="Exporter cette réponse en TXT"
              >
                {exportLoading.txt ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" style={{ width: '0.6rem', height: '0.6rem' }}></span>
                    Export TXT...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-text me-1"></i>
                    Exporter TXT
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Actions suggérées */}
        {message.suggestedActions && message.suggestedActions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.suggestedActions.map((action, index) => (
              <button
                key={index}
                onClick={() => onActionClick && onActionClick(action)}
                className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-full transition-colors cursor-pointer"
              >
                {action}
              </button>
            ))}
          </div>
        )}

        <div className={`text-xs mt-2 opacity-75 ${message.type === 'user' ? 'text-blue-100' : 'text-slate-500'
          }`}>
          {message.timestamp.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      </div>
    </div>
  );
};

const ChatBot = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: "👋 Bonjour ! Je suis votre assistant IA pour analyser vos emails et messages WhatsApp",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailStats, setEmailStats] = useState({ totalEmails: 0 });
  const [conversationHistory, setConversationHistory] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    scrollToBottom();
    // Ne faire l'appel API que si l'utilisateur est authentifié
    if (isAuthenticated) {
      loadEmailStats();
      // Créer une session si elle n'existe pas
      if (!sessionId) {
        createChatSession();
      }
    }
  }, [messages, isAuthenticated]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadEmailStats = async () => {
    try {
      const stats = await emailService.getStats();
      setEmailStats(stats);
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
    }
  };

  const createChatSession = async () => {
    try {
      const response = await emailService.createChatSession();
      setSessionId(response.session_id);
      console.log('Session créée:', response.session_id);
    } catch (error) {
      console.error('Erreur lors de la création de session:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentQuestion = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      // Préparer le contexte pour l'IA
      let context = {
        stats: emailStats,
        timestamp: new Date().toISOString()
      };

      // Récupérer des emails récents pour contexte si nécessaire
      if (currentQuestion.toLowerCase().includes('email') ||
        currentQuestion.toLowerCase().includes('résume') ||
        currentQuestion.toLowerCase().includes('correspondant')) {
        try {
          const emailsData = await emailService.getEmails(1, 20);
          context.recent_emails = emailsData.emails.map(email => ({
            subject: email.subject,
            sender: email.sender,
            date: email.date,
            content: email.content.substring(0, 1000) // Augmenter la limite pour plus de contexte
          }));
          context.total_emails = emailsData.pagination.total;
        } catch (e) {
          console.warn('Impossible de charger les emails pour contexte:', e);
        }
      }

      // Appeler l'API chatbot intelligente avec session et historique
      const response = await emailService.askChatBot(
        currentQuestion,
        context,
        sessionId,
        conversationHistory
      );

      // Ajouter historique de conversation
      const newHistory = [...conversationHistory, {
        question: currentQuestion,
        response: response.response,
        type: response.response_type
      }];
      setConversationHistory(newHistory);

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: response.response,
        timestamp: new Date(),
        responseType: response.response_type,
        suggestedActions: response.suggested_actions || [],
        processingTime: response.processing_time
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Erreur lors de l\'appel au chatbot:', error);

      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: "😔 Désolé, j'ai rencontré une erreur lors du traitement de votre question. Cela peut être dû à :\n\n• Service IA temporairement indisponible\n• Problème de connexion\n• Question trop complexe\n\n💡 **Suggestions :**\n• Reformulez votre question plus simplement\n• Vérifiez que vos emails sont bien importés\n• Réessayez dans quelques instants",
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSendMessage();
    }
    // Shift+Entrée permet une nouvelle ligne (comportement par défaut)
  };

  const handleSuggestedQuestion = (question) => {
    setInputMessage(question);
  };

  const handleActionClick = (action) => {
    // Convertir les actions suggérées en nouvelles questions
    const actionQuestions = {
      'Voir plus de résultats': 'Affiche-moi plus de résultats de recherche',
      'Voir analytics détaillés': 'Donne-moi des statistiques détaillées sur mes emails',
      'Analyser plus d\'emails': 'Comment analyser plus d\'emails ?',
      'Voir détails complets': 'Donne-moi tous les détails sur cette analyse',
      'Analyser tendances': 'Quelles sont les tendances dans mes emails ?',
      'Voir analyse complète': 'Affiche-moi l\'analyse complète de sentiment',
      'Améliorer communication': 'Comment améliorer la communication dans mes emails ?',
      'Explorer fonctionnalités': 'Quelles sont toutes les fonctionnalités disponibles ?',
      'Voir aide': 'Comment utiliser Discover ?',
      'Commencer analyse': 'Comment commencer l\'analyse de mes emails ?'
    };

    const question = actionQuestions[action] || action;
    setInputMessage(question);
  };

  const suggestedQuestions = [
    "Qui sont mes correspondants principaux ?"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="container-fluid py-8">
        <div className="max-w-5xl mx-auto">

          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center animate-pulse">
                  <i className="bi bi-robot text-white text-2xl"></i>
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-400 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-green-800">AI</span>
                </div>
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-2">
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Assistant IA Discover
              </span>
            </h1>
            <p className="text-slate-600 mb-2">
              {emailStats.totalEmails} emails disponibles
            </p>
            <div className="flex justify-center items-center space-x-4 text-sm text-slate-500">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Service IA actif</span>
              </div>
              <span>•</span>
              <span>Temps de réponse: ~2-5s</span>
            </div>
          </div>

          {/* Chat Container */}
          <div className="glass rounded-2xl shadow-2xl overflow-hidden">

            {/* Messages */}
            <div className="h-[300px] overflow-y-auto p-4 space-y-3 chatbot-messages-area">
              {messages.map((message) => (
                <MessageWrapper
                  key={message.id}
                  message={message}
                  onActionClick={handleActionClick}
                />
              ))}

              {/* Indicateur de frappe */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white text-slate-800 border border-slate-200 mr-12 px-4 py-3 rounded-2xl shadow-lg">
                    <div className="flex items-center space-x-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-sm text-slate-600">Assistant IA réfléchit...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Questions suggérées */}
            {messages.length <= 1 && (
              <div className="px-6 py-3 bg-slate-50 border-t border-slate-200">
                <p className="text-sm text-slate-600 mb-2 font-medium">💡 Question suggérée :</p>
                <div className="flex justify-center">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestedQuestion(question)}
                      className="p-2 text-xs bg-white hover:bg-blue-50 text-slate-700 hover:text-blue-700 rounded-lg border border-slate-200 hover:border-blue-300 transition-all text-left"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Zone de saisie optimisée */}
            <div className="chatbot-input-area p-4">
              <div className="flex flex-col lg:flex-row space-y-3 lg:space-y-0 lg:space-x-4">
                <div className="flex-1 relative">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Posez votre question sur vos emails et messages WhatsApp... (Analyses juridiques, tableaux synthétiques, recherches croisées, etc.)"
                    className="w-full form-control pr-12 focus:ring-blue-500 focus:border-blue-500 resize-none chatbot-textarea"
                    style={{
                      minHeight: '80px',
                      maxHeight: '160px',
                      fontSize: '15px'
                    }}
                    rows={3}
                    disabled={isLoading}
                    maxLength={5000}
                  />
                  <div className="absolute right-3 bottom-3 text-xs text-slate-400 chatbot-char-counter">
                    {inputMessage.length}/5000
                  </div>
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  className="chatbot-send-button px-6 py-3 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed self-end lg:self-start"
                  style={{ minHeight: '80px', minWidth: '120px' }}
                >
                  {isLoading ? (
                    <>
                      <div className="spinner-border spinner-border-sm"></div>
                      <span>Envoi...</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send"></i>
                      <span>Envoyer</span>
                    </>
                  )}
                </button>
              </div>

              <div className="flex justify-between items-center mt-3 text-xs text-slate-500">
                <span>Appuyez sur Ctrl+Entrée pour envoyer • Shift+Entrée pour nouvelle ligne</span>
                <span className="text-blue-600">✨ Support des tableaux HTML et analyses juridiques</span>
              </div>
            </div>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
            <div className="card p-4 text-center hover:shadow-lg transition-shadow">
              <i className="bi bi-search text-blue-600 text-2xl mb-2"></i>
              <h3 className="font-semibold text-slate-800 mb-1">Recherche</h3>
              <p className="text-xs text-slate-600">Trouvez des emails spécifiques</p>
            </div>

            <div className="card p-4 text-center hover:shadow-lg transition-shadow">
              <i className="bi bi-graph-up text-green-600 text-2xl mb-2"></i>
              <h3 className="font-semibold text-slate-800 mb-1">Analytics</h3>
              <p className="text-xs text-slate-600">Statistiques intelligentes</p>
            </div>

            <div className="card p-4 text-center hover:shadow-lg transition-shadow">
              <i className="bi bi-emoji-smile text-yellow-600 text-2xl mb-2"></i>
              <h3 className="font-semibold text-slate-800 mb-1">Sentiment</h3>
              <p className="text-xs text-slate-600">Analyse émotionnelle</p>
            </div>

            <div className="card p-4 text-center hover:shadow-lg transition-shadow">
              <i className="bi bi-lightbulb text-purple-600 text-2xl mb-2"></i>
              <h3 className="font-semibold text-slate-800 mb-1">Insights</h3>
              <p className="text-xs text-slate-600">Découvertes automatiques</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ChatBot;
